// Enhanced Portfolio Website with Core Features
// Author: CodeWithMishu
// Features: Particle System, Command Palette

// === Import Styles ===
import './style.css';
import emailjs from "@emailjs/browser"

// === DEV MODE: Auto-unregister Service Worker ===
const isDevelopment = location.hostname === 'localhost' || 
                      location.hostname === '127.0.0.1' || 
                      location.hostname === '' ||
                      location.port === '5173' || // Vite dev server
                      location.port === '3000';   // Alternative dev port

if (isDevelopment) {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(reg => {
        reg.unregister();
        console.log('🔧 Dev mode: Service worker unregistered');
      });
    });
    caches.keys().then(keys => {
      Promise.all(keys.map(k => caches.delete(k)));
      console.log('🔧 Dev mode: All caches cleared');
    });
  }
  // Store dev mode flag in sessionStorage
  sessionStorage.setItem('devMode', 'true');
  
  // Add visual dev mode indicator
  const devIndicator = document.createElement('div');
  devIndicator.id = 'dev-indicator';
  devIndicator.innerHTML = 'DEV';
  devIndicator.className = 'fixed top-2 left-2 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded z-50 opacity-75 hover:opacity-100 transition-opacity';
  devIndicator.title = 'Development Mode - Service Worker Disabled';
  document.addEventListener('DOMContentLoaded', () => {
    document.body.appendChild(devIndicator);
  });
}

document.addEventListener('DOMContentLoaded', function () {
  // Core elements
  const themeToggleBtn = document.getElementById('theme-toggle');
  const buttons = Array.from(document.querySelectorAll('.button'));
  // Theme colors configuration
  const themeColors = [
    { primary: '#FF6F91', light: '#FFE5EC', accent: '#38B6FF', name: 'Pink Paradise' },
    { primary: '#38B6FF', light: '#E8F6F6', accent: '#FF9671', name: 'Ocean Blue' },
    { primary: '#FF9671', light: '#FFF5E4', accent: '#FF6F91', name: 'Sunset Orange' },
    { primary: '#9B5DE5', light: '#EDE7F6', accent: '#00BBF9', name: 'Purple Magic' },
    { primary: '#00BBF9', light: '#F0F9FF', accent: '#9B5DE5', name: 'Sky Blue' },
    { primary: '#F15BB5', light: '#FCE7F3', accent: '#9B59B6', name: 'Hot Pink' },
    { primary: '#00F5FF', light: '#F0FFFF', accent: '#FF4081', name: 'Cyan Fresh' },
    { primary: '#FEE715', light: '#FFFBEB', accent: '#E74C3C', name: 'Golden Yellow' },
    { primary: '#C44569', light: '#FAE5EE', accent: '#40739E', name: 'Berry Wine' },
    { primary: '#1DD1A1', light: '#ECFDF5', accent: '#FF6B35', name: 'Mint Green' }
  ];

  let currentTheme = 0;
  let socialColorIndex = 0;

  // === PARTICLE SYSTEM ===
  class ParticleSystem {
    constructor() {
      this.canvas = document.getElementById('particle-canvas');
      this.ctx = this.canvas.getContext('2d');
      this.particles = [];
      this.init();
    }

    init() {
      this.resize();
      this.createParticles();
      this.animate();
      window.addEventListener('resize', () => this.resize());
    }

    resize() {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    }

    createParticles() {
      for (let i = 0; i < 50; i++) {
        this.particles.push({
          x: Math.random() * this.canvas.width,
          y: Math.random() * this.canvas.height,
          vx: (Math.random() - 0.5) * 2,
          vy: (Math.random() - 0.5) * 2,
          size: Math.random() * 3 + 1,
          opacity: Math.random() * 0.5 + 0.2
        });
      }
    }

    animate() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      this.particles.forEach(particle => {
        particle.x += particle.vx;
        particle.y += particle.vy;

        if (particle.x < 0 || particle.x > this.canvas.width) particle.vx *= -1;
        if (particle.y < 0 || particle.y > this.canvas.height) particle.vy *= -1;

        this.ctx.beginPath();
        this.ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        this.ctx.fillStyle = `rgba(255, 111, 145, ${particle.opacity})`;
        this.ctx.fill();
      });

      requestAnimationFrame(() => this.animate());
    }
  }

  // === MOUSE TRAIL EFFECT ===
  class MouseTrail {
    constructor() {
      this.trail = document.getElementById('mouse-trail');
      this.init();
    }

    init() {
      document.addEventListener('mousemove', (e) => {
        this.trail.style.left = e.clientX + 'px';
        this.trail.style.top = e.clientY + 'px';
        this.trail.style.opacity = '1';
      });

      document.addEventListener('mouseleave', () => {
        this.trail.style.opacity = '0';
      });
    }
  }
  // === COMMAND PALETTE ===
  class CommandPalette {
    constructor() {
      this.palette = document.getElementById('command-palette');
      this.input = document.getElementById('command-input');
      this.suggestions = document.getElementById('command-suggestions'); this.commands = {
        'theme': () => this.executeCommand('Switching theme...', () => themeToggleBtn.click()),
        'clear': () => this.executeCommand('Clearing console...', () => console.clear()),
        'reload': () => this.executeCommand('Reloading page...', () => window.location.reload()),
        'fullscreen': () => this.executeCommand('Toggling fullscreen...', () => this.toggleFullscreen()),
        'clear cache': () => this.executeCommand('Clearing cache...', () => this.clearAllCaches())
      };
      this.init();
    }

    init() {
      document.addEventListener('keydown', (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
          e.preventDefault();
          this.toggle();
        }
        if (e.key === 'Escape') {
          this.hide();
        }
      });

      this.input.addEventListener('input', () => this.updateSuggestions());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          this.executeFirstSuggestion();
        }
      });
    }

    toggle() {
      if (this.palette.classList.contains('hidden')) {
        this.show();
      } else {
        this.hide();
      }
    }

    show() {
      this.palette.classList.remove('hidden');
      this.input.focus();
      this.updateSuggestions();
    }

    hide() {
      this.palette.classList.add('hidden');
      this.input.value = '';
    }

    updateSuggestions() {
      const query = this.input.value.toLowerCase();
      const matches = Object.keys(this.commands).filter(cmd =>
        cmd.includes(query)
      );

      this.suggestions.innerHTML = matches.map(cmd =>
        `<div class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer rounded" 
              onclick="commandPalette.executeCommand('${cmd}', commandPalette.commands['${cmd}'])">${cmd}</div>`
      ).join('');
    }

    executeFirstSuggestion() {
      const firstCmd = this.suggestions.querySelector('div');
      if (firstCmd) {
        firstCmd.click();
      }
    }

    executeCommand(message, action) {
      this.showToast(message);
      this.hide();
      setTimeout(action, 300);
    }

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    }

    async clearAllCaches() {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(cache => caches.delete(cache)));
        console.log('🧹 All caches cleared');
      }
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(reg => reg.unregister()));
        console.log('🔧 Service worker unregistered');
      }
      this.showToast('Cache cleared! Refreshing...');
      setTimeout(() => window.location.reload(), 1000);
    }

    showToast(message) {
      const toast = document.createElement('div');
      toast.className = 'toast';
      toast.textContent = message;
      document.body.appendChild(toast);

      setTimeout(() => toast.classList.add('show'), 100);
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => document.body.removeChild(toast), 300);
      }, 2000);
    }
  }
  function setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // No additional shortcuts currently active
    });
  }

  function requestNotificationPermission() {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  // Theme toggle functionality
  const themeIconMoon = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>';
  const themeIconSun = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>';
  const themeIcon = document.getElementById('theme-icon');

  if (themeToggleBtn && themeIcon) {
    themeToggleBtn.addEventListener('click', () => {
      document.documentElement.classList.toggle('dark');
      themeIcon.outerHTML = document.documentElement.classList.contains('dark') ? themeIconSun : themeIconMoon;
    });
  }
  // Enhanced scroll functionality with color cycling
  function updateGlobalScrollIndicators(pct) {
    const roundedPct = Math.round(pct);
    const clampedPct = Math.max(0, Math.min(100, pct));

    // Update border progress indicators
    document.getElementById('progress-top').style.width = clampedPct + '%';
    document.getElementById('progress-right').style.height = clampedPct + '%';
    document.getElementById('progress-bottom').style.width = clampedPct + '%';
    document.getElementById('progress-left').style.height = clampedPct + '%';

    // Change theme every 25% scroll
    const themeBasedOnScroll = Math.floor(clampedPct / 25);
    if (themeBasedOnScroll !== currentTheme && themeBasedOnScroll < themeColors.length) {
      currentTheme = themeBasedOnScroll;
      updateThemeColors();
    }
  }
  // Enhanced social button interactions
  function setupSocialButtons() {
    const socialButtons = document.querySelectorAll('.social-button');
    socialButtons.forEach(button => {
      button.addEventListener('mouseenter', () => {
        button.style.transform = 'translateY(-2px) scale(1.05)';
      });

      button.addEventListener('mouseleave', () => {
        button.style.transform = 'translateY(0) scale(1)';
      });

      button.addEventListener('click', (e) => {
        // Add click ripple effect
        const rect = button.getBoundingClientRect();
        const ripple = document.createElement('span');
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.className = 'absolute bg-white opacity-30 rounded-full pointer-events-none';
        ripple.style.animation = 'ripple 0.6s ease-out';

        button.appendChild(ripple);
        setTimeout(() => {
          if (ripple.parentNode) {
            ripple.parentNode.removeChild(ripple);
          }
        }, 600);
      });
    });
  }

  // Enhanced theme cycling with comprehensive color updates
  function updateThemeColors() {
    const theme = themeColors[currentTheme];

    // Update CSS custom properties
    document.documentElement.style.setProperty('--primary', theme.primary);
    document.documentElement.style.setProperty('--primary-light', theme.light);
    document.documentElement.style.setProperty('--accent', theme.accent);

    // Update background
    document.body.style.backgroundColor = theme.light;

    // Update brand text elements
    const brandElements = document.querySelectorAll('.text-brand, h1.text-brand');
    brandElements.forEach(el => el.style.color = theme.primary);

    // Update profile image border
    const profileImg = document.querySelector('img.border-4');
    if (profileImg) profileImg.style.borderColor = theme.primary;

    // Update theme button
    const themeBtn = document.getElementById('theme-toggle');
    if (themeBtn) {
      themeBtn.style.backgroundColor = theme.primary;
    }

    // Update progress indicators
    const progressElements = document.querySelectorAll('[id^="progress-"]');
    progressElements.forEach(el => el.style.backgroundColor = theme.primary);

    // Update skill section headers
    const skillHeaders = document.querySelectorAll('#skills-section h2');
    skillHeaders.forEach(el => {
      el.style.background = `linear-gradient(45deg, ${theme.primary}, ${theme.accent})`;
      el.style.webkitBackgroundClip = 'text';
      el.style.webkitTextFillColor = 'transparent';
      el.style.backgroundClip = 'text';
    });

    // Update skill progress bars
    const skillBars = document.querySelectorAll('.skill-progress');
    skillBars.forEach(el => el.style.backgroundColor = theme.primary);

    // Update article dividers
    const dividers = document.querySelectorAll('.bg-gradient-to-r');
    dividers.forEach(el => {
      if (el.classList.contains('via-brand')) {
        el.style.background = `linear-gradient(to right, transparent, ${theme.primary}, transparent)`;
      }
    });

    // Show theme name notification
    showThemeNotification(theme.name);
  }

  // Social media button color cycling
  function cycleSocialButtonColors() {
    const socialButtons = document.querySelectorAll('.social-button');
    const colors = [
      { from: '#C0392B', to: '#A93226' }, // Red
      { from: '#0E7C7B', to: '#0B5E5D' }, // Teal
      { from: '#1A56DB', to: '#1E40AF' }, // Blue
      { from: '#1E8449', to: '#166534' }, // Green
      { from: '#B8860B', to: '#9A6E08' }, // Gold
      { from: '#7C3AED', to: '#5B21B6' }, // Violet
      { from: '#BE185D', to: '#9D174D' }, // Pink
      { from: '#C2410C', to: '#9A3412' }, // Orange
      { from: '#1E293B', to: '#0F172A' }, // Slate
      { from: '#831843', to: '#5B122F' }  // Rose
    ];

    socialButtons.forEach((button, index) => {
      const colorIndex = (socialColorIndex + index) % colors.length;
      const color = colors[colorIndex];
      button.style.background = `linear-gradient(135deg, ${color.from}, ${color.to})`;
      button.style.transform = 'scale(1.02)';

      setTimeout(() => {
        button.style.transform = 'scale(1)';
      }, 200);
    });

    socialColorIndex = (socialColorIndex + 1) % colors.length;
  }

  // Random theme cycling
  function startRandomColorCycling() {
    setInterval(() => {
      currentTheme = Math.floor(Math.random() * themeColors.length);
      updateThemeColors();
    }, 5000); // Change theme every 5 seconds
  }

  // Theme notification
  function showThemeNotification(themeName) {
    // Remove existing notification
    const existingNotification = document.querySelector('.theme-notification');
    if (existingNotification) {
      existingNotification.remove();
    }

    const notification = document.createElement('div');
    notification.className = 'theme-notification fixed top-20 right-4 bg-black/80 text-white px-4 py-2 rounded-lg z-50 transform translate-x-full transition-transform duration-300';
    notification.textContent = `Theme: ${themeName}`;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    setTimeout(() => {
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 2000);
  }
  // Initialize everything
  function init() {
    // Initialize core systems
    new ParticleSystem();
    new MouseTrail();

    // Initialize command palette
    window.commandPalette = new CommandPalette();

    // Setup functionality
    setupSocialButtons();

    // Start color cycling features
    startRandomColorCycling();
    cycleSocialButtonColors(); // Apply cycling colors immediately to avoid flash from static gradients
    setInterval(cycleSocialButtonColors, 2000); // Change social button colors every 2 seconds

    // Setup scroll functionality
    window.addEventListener('scroll', () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.min(100, (scrollTop / scrollHeight) * 100);
      updateGlobalScrollIndicators(scrollPercent);
    });

    // Setup typing animation
    const typingElement = document.getElementById('typing');
    if (typingElement) {
      const texts = [
        "Full Stack Developer",
        "AI/ML Enthusiast",
        "Content Creator",
        "Tech Educator"
      ];
      let textIndex = 0;
      let charIndex = 0;
      let isDeleting = false;

      function typeText() {
        const currentText = texts[textIndex];

        if (isDeleting) {
          typingElement.textContent = currentText.substring(0, charIndex - 1);
          charIndex--;
        } else {
          typingElement.textContent = currentText.substring(0, charIndex + 1);
          charIndex++;
        }

        if (!isDeleting && charIndex === currentText.length) {
          setTimeout(() => isDeleting = true, 2000);
        } else if (isDeleting && charIndex === 0) {
          isDeleting = false;
          textIndex = (textIndex + 1) % texts.length;
        }

        setTimeout(typeText, isDeleting ? 50 : 100);
      }

      typeText();
    }
  }

  // === INDEPENDENT SCROLLING SYSTEM ===
  class IndependentScrolling {
    constructor() {
      this.sidebar = document.getElementById('sidebar');
      this.mainContent = document.getElementById('main-content');
      this.isDesktop = window.innerWidth >= 1024;
      this.init();
    }

    init() {
      if (!this.isDesktop) return;

      // Prevent default scroll on body when over specific areas
      this.sidebar.addEventListener('wheel', (e) => {
        e.stopPropagation();
        // Allow normal scrolling within sidebar
      });

      this.mainContent.addEventListener('wheel', (e) => {
        e.stopPropagation();
        // Allow normal scrolling within main content
      });

      // Handle window resize
      window.addEventListener('resize', () => {
        this.isDesktop = window.innerWidth >= 1024;
        if (!this.isDesktop) {
          this.resetMobileLayout();
        }
      });
    }

    resetMobileLayout() {
      // Reset any specific desktop behaviors for mobile
      this.sidebar.style.position = 'relative';
      this.sidebar.style.width = '100%';
      this.sidebar.style.height = 'auto';
      this.mainContent.style.marginLeft = '0';
      this.mainContent.style.height = 'auto';
    }
  }

  // === MOBILE APP DEEP LINKING SYSTEM ===
  class MobileAppDeepLinks {
    constructor() {
      this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      this.isAndroid = /Android/.test(navigator.userAgent);

      // Deep link mappings
      this.deepLinks = {
        youtube: {
          ios: 'youtube://www.youtube.com/@CodeWithMishu', // Updated YouTube URL format
          android: 'intent://www.youtube.com/@CodeWithMishu#Intent;scheme=https;package=com.google.android.youtube;S.browser_fallback_url=https://youtube.com/@CodeWithMishu;end',
          fallback: 'https://youtube.com/@CodeWithMishu'
        },
        instagram: {
          ios: 'instagram://user?username=codewithmishu',
          android: 'intent://instagram.com/codewithmishu#Intent;scheme=https;package=com.instagram.android;S.browser_fallback_url=https://instagram.com/codewithmishu;end',
          fallback: 'https://instagram.com/codewithmishu'
        },
        linkedin: {
          ios: 'linkedin://profile/codewithmishu',
          android: 'intent://linkedin.com/in/codewithmishu#Intent;scheme=https;package=com.linkedin.android;S.browser_fallback_url=https://linkedin.com/in/codewithmishu;end',
          fallback: 'https://linkedin.com/in/codewithmishu'
        },
        twitter: {
          ios: 'twitter://user?screen_name=CodeWithMishu',
          android: 'intent://x.com/CodeWithMishu#Intent;scheme=https;package=com.twitter.android;S.browser_fallback_url=https://x.com/CodeWithMishu;end',
          fallback: 'https://x.com/CodeWithMishu'
        },
        github: {
          ios: 'github://CodeWithMishu', // GitHub app scheme
          android: 'intent://github.com/CodeWithMishu#Intent;scheme=https;package=com.github.android;S.browser_fallback_url=https://github.com/CodeWithMishu;end',
          fallback: 'https://github.com/CodeWithMishu'
        }
      };

      this.init();
    }

    init() {
      if (this.isMobile) {
        this.setupMobileLinks();
      }
    }

    setupMobileLinks() {
      // YouTube link
      const youtubeBtn = document.querySelector('.youtube-btn');
      if (youtubeBtn) {
        youtubeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openApp('youtube');
        });
      }

      // Instagram link
      const instagramBtn = document.querySelector('.instagram-btn');
      if (instagramBtn) {
        instagramBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openApp('instagram');
        });
      }

      // LinkedIn link
      const linkedinBtn = document.querySelector('.linkedin-btn');
      if (linkedinBtn) {
        linkedinBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openApp('linkedin');
        });
      }

      // Twitter/X link
      const xBtn = document.querySelector('.x-btn');
      if (xBtn) {
        xBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openApp('twitter');
        });
      }

      // GitHub link
      const githubBtn = document.querySelector('.github-btn');
      if (githubBtn) {
        githubBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openApp('github');
        });
      }
    }

    openApp(platform) {
      const links = this.deepLinks[platform];
      if (!links) {
        return;
      }

      let appUrl = links.fallback; // Default fallback

      if (this.isIOS && links.ios) {
        appUrl = links.ios;
      } else if (this.isAndroid && links.android) {
        appUrl = links.android;
      }

      // Try to open the app
      if (this.isIOS && links.ios) {
        // For iOS, try app scheme first, then fallback to web
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = links.ios;
        document.body.appendChild(iframe);

        // Fallback to web if app doesn't open within 1 second
        setTimeout(() => {
          document.body.removeChild(iframe);
          window.open(links.fallback, '_blank');
        }, 1000);

        // Remove iframe after attempting to open app
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 100);

      } else {
        // For Android or fallback, open directly
        window.open(appUrl, '_blank');
      }
    }

    // Utility method to show a toast notification for mobile users
    showMobileToast() {
      if (!this.isMobile) return;

      const toast = document.createElement('div');
      toast.className = 'fixed bottom-4 left-4 right-4 bg-brand text-white p-3 rounded-lg shadow-lg z-50 text-center transform translate-y-full transition-transform duration-300';
      toast.innerHTML = 'Tap buttons to open in apps!';
      document.body.appendChild(toast);

      // Show toast
      setTimeout(() => {
        toast.style.transform = 'translateY(0)';
      }, 100);

      // Hide toast after 3 seconds
      setTimeout(() => {
        toast.style.transform = 'translateY(100%)';
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 3000);
    }
  }

  // === GITHUB PROJECTS INTEGRATION ===
  class GitHubProjects {
    constructor() {
      this.username = 'CodeWithMishu';
      this.projectsContainer = document.getElementById('projects-grid');
      this.loadingElement = document.getElementById('projects-loading');
      this.skeletonElement = document.getElementById('projects-skeleton');
      this.init();
    }

    async init() {
      try {
        await this.fetchProjects();
        await this.fetchStats();
      } catch (error) {
        console.log('GitHub API rate limited, showing demo data');
        this.showDemoProjects();
        this.showDemoStats();
      }
    }

    async fetchProjects() {
      const response = await fetch(`https://api.github.com/users/${this.username}/repos?sort=updated&per_page=100`);
      const repos = await response.json();

      // Featured project names you want to showcase
      const featuredRepoNames = [
        'university_attendance_system',
        'Ginkgo-Mart-ecommerce'
      ];

      // Filter repos to show only the featured ones in order
      const featuredProjects = featuredRepoNames
        .map(name => repos.find(repo => repo.name === name))
        .filter(repo => repo && !repo.fork);

      // If we found featured projects, use them; otherwise fallback to general featured projects
      if (featuredProjects.length > 0) {
        this.renderProjects(featuredProjects);
      } else {
        const fallbackProjects = repos.filter(repo => !repo.fork && repo.description).slice(0, 6);
        this.renderProjects(fallbackProjects);
      }
    }

    async fetchStats() {
      try {
        const [userResponse, reposResponse] = await Promise.all([
          fetch(`https://api.github.com/users/${this.username}`),
          fetch(`https://api.github.com/users/${this.username}/repos?per_page=100`)
        ]);

        const userData = await userResponse.json();
        const reposData = await reposResponse.json();

        const totalStars = reposData.reduce((sum, repo) => sum + repo.stargazers_count, 0);

        this.updateStats({
          repos: userData.public_repos,
          stars: totalStars,
          visitors: Math.floor(Math.random() * 100) + 50, // Demo data
          streak: Math.floor(Math.random() * 30) + 10 // Demo data
        });
      } catch (error) {
        this.showDemoStats();
      }
    }

    renderProjects(projects) {
      this.loadingElement.classList.add('hidden');
      if (this.skeletonElement) this.skeletonElement.classList.add('hidden');
      this.projectsContainer.classList.remove('hidden');

      this.projectsContainer.innerHTML = projects.map(project => `
        <div class="project-card bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 hover:scale-105 group">
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-brand transition-colors">
                ${project.name}
              </h3>
              <p class="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                ${project.description || 'An awesome project built with love'}
              </p>
            </div>
            <div class="project-icon">
              ${this.getProjectIcon(project.language)}
            </div>
          </div>
          
          <div class="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
            <span class="flex items-center">
              <span class="w-3 h-3 rounded-full mr-2" style="background-color: ${this.getLanguageColor(project.language)}"></span>
              ${project.language || 'Code'}
            </span>
            <div class="flex items-center space-x-4">
              <span class="flex items-center">
                ⭐ ${project.stargazers_count}
              </span>
              <span class="flex items-center">
                🍴 ${project.forks_count}
              </span>
            </div>
          </div>
          
          <div class="flex space-x-3">
            <a 
              href="${project.html_url}" 
              target="_blank"
              class="flex-1 bg-brand hover:bg-brand-hover text-white text-center py-2 px-4 rounded-lg font-medium transition-all duration-300 hover:scale-105"
            >
              View Code
            </a>
            ${project.homepage ? `
              <a 
                href="${project.homepage}" 
                target="_blank"
                class="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-center py-2 px-4 rounded-lg font-medium transition-all duration-300 hover:scale-105"
              >
                Live Demo
              </a>
            ` : ''}
          </div>
        </div>
      `).join('');
    }

    showDemoProjects() {
      const demoProjects = [
        {
          name: 'university_attendance_system',
          description: 'Smart attendance management system with AI face recognition using InsightFace. Real-time monitoring, WhatsApp/Email notifications, MongoDB database, supports 3000+ students.',
          language: 'Python',
          stargazers_count: 24,
          forks_count: 7,
          html_url: 'https://github.com/CodeWithMishu/university_attendance_system',
          homepage: 'https://university-attendance-system.vercel.app'
        },
        {
          name: 'Ginkgo-Mart-ecommerce',
          description: 'Fully responsive e-commerce platform for millet products. Built with vanilla JavaScript, modern CSS grid, dynamic product cards, shopping cart functionality, and smooth animations.',
          language: 'JavaScript',
          stargazers_count: 32,
          forks_count: 9,
          html_url: 'https://github.com/CodeWithMishu/Ginkgo-Mart-ecommerce',
          homepage: null
        }
      ];

      this.renderProjects(demoProjects);
    }

    showDemoStats() {
      this.updateStats({
        repos: 25,
        stars: 89,
        visitors: 147,
        streak: 23
      });
    }

    updateStats(stats) {
      const counters = {
        'github-repos': stats.repos,
        'github-stars': stats.stars,
        'visitor-count': stats.visitors,
        'commit-streak': stats.streak
      };

      // Animate counter updates
      Object.entries(counters).forEach(([id, finalValue]) => {
        const element = document.getElementById(id);
        if (element) {
          this.animateCounter(element, finalValue);
        }
      });

      // Update last updated time
      const lastUpdated = document.getElementById('last-updated');
      if (lastUpdated) {
        lastUpdated.textContent = `Updated ${new Date().toLocaleTimeString()}`;
      }
    }

    animateCounter(element, finalValue) {
      let current = 0;
      const increment = finalValue / 30;
      const timer = setInterval(() => {
        current += increment;
        if (current >= finalValue) {
          current = finalValue;
          clearInterval(timer);
        }
        element.textContent = Math.floor(current);
      }, 50);
    }

    getProjectIcon(language) {
      const icons = {
        'JavaScript': '<i class="devicon-javascript-plain"></i>',
        'Python': '<i class="devicon-python-plain"></i>',
        'TypeScript': '<i class="devicon-typescript-plain"></i>',
        'React': '<i class="devicon-react-original"></i>',
        'Node.js': '<i class="devicon-nodejs-plain"></i>',
        'HTML': '<i class="devicon-html5-plain"></i>',
        'CSS': '<i class="devicon-css3-plain"></i>',
        'Java': '<i class="devicon-java-plain"></i>',
        'C++': '<i class="devicon-cplusplus-plain"></i>',
        'Go': '<i class="devicon-go-plain"></i>'
      };
      return icons[language] || '<i class="devicon-devicon-plain"></i>';
    }

    getLanguageColor(language) {
      const colors = {
        'JavaScript': '#f1e05a',
        'Python': '#3572A5',
        'TypeScript': '#2b7489',
        'HTML': '#e34c26',
        'CSS': '#563d7c',
        'Java': '#b07219',
        'C++': '#f34b7d',
        'Go': '#00ADD8'
      };
      return colors[language] || '#6e7681';
    }
  }

  // 📱 PROGRESSIVE WEB APP (PWA) MANAGER
  class PWAManager {
    constructor() {
      this.deferredPrompt = null;
      this.init();
    }

    async init() {
      await this.registerServiceWorker();
      this.handleInstallPrompt();
      this.addInstallButton();
      this.handleOfflineStatus();
    }

    async registerServiceWorker() {
      // Don't register service worker in development mode
      const isDev = sessionStorage.getItem('devMode') === 'true' || 
                    location.hostname === 'localhost' ||
                    location.hostname === '127.0.0.1' ||
                    location.port === '5173' ||
                    location.port === '3000';
      
      if (isDev) {
        console.log('🔧 Dev mode: Service worker registration skipped');
        return;
      }

      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.register('/service-worker.js');
          console.log('Service Worker registered successfully:', registration.scope);

          // Handle service worker updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                this.showUpdateNotification();
              }
            });
          });
        } catch (error) {
          console.error('❌ Service Worker registration failed:', error);
        }
      }
    }

    handleInstallPrompt() {
      window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        this.deferredPrompt = event;
        this.showInstallButton();
      });

      window.addEventListener('appinstalled', () => {
        console.log('PWA installed successfully');
        this.hideInstallButton();
        this.showInstallSuccess();
      });
    }

    addInstallButton() {
      const installButton = document.createElement('button');
      installButton.id = 'pwa-install-btn';
      installButton.innerHTML = 'Install App';
      installButton.className = 'hidden fixed bottom-20 right-4 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-300 z-50 text-sm font-medium';
      installButton.addEventListener('click', () => this.installPWA());
      document.body.appendChild(installButton);
    }

    showInstallButton() {
      const button = document.getElementById('pwa-install-btn');
      if (button) {
        button.classList.remove('hidden');
        button.style.animation = 'pulse 2s ease-in-out infinite';
      }
    }

    hideInstallButton() {
      const button = document.getElementById('pwa-install-btn');
      if (button) {
        button.classList.add('hidden');
      }
    }

    async installPWA() {
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        const { outcome } = await this.deferredPrompt.userChoice;

        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
        } else {
          console.log('User dismissed the install prompt');
        }

        this.deferredPrompt = null;
        this.hideInstallButton();
      }
    }

    showUpdateNotification() {
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-3';
      notification.innerHTML = `
        <span>App updated! Refresh to get the latest version.</span>
        <button onclick="window.location.reload()" class="bg-white text-blue-500 px-3 py-1 rounded text-sm font-medium hover:bg-gray-100 transition-colors">
          Refresh
        </button>
        <button onclick="this.parentElement.remove()" class="text-white hover:text-gray-200 text-xl">&times;</button>
      `;
      document.body.appendChild(notification);
    }

    showInstallSuccess() {
      const success = document.createElement('div');
      success.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300';
      success.innerHTML = 'App installed successfully!';
      document.body.appendChild(success);

      setTimeout(() => {
        success.style.transform = 'translateX(0)';
      }, 100);

      setTimeout(() => {
        success.style.transform = 'translateX(100%)';
        setTimeout(() => {
          if (document.body.contains(success)) {
            document.body.removeChild(success);
          }
        }, 300);
      }, 3000);
    }

    handleOfflineStatus() {
      const showConnectionStatus = (isOnline) => {
        const existing = document.getElementById('connection-status');
        if (existing) existing.remove();

        const status = document.createElement('div');
        status.id = 'connection-status';
        status.className = `fixed bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg z-50 text-sm font-medium transition-all duration-300 ${isOnline
          ? 'bg-green-500 text-white'
          : 'bg-red-500 text-white'
          }`;
        status.innerHTML = isOnline ? 'Back online!' : 'You\'re offline';
        document.body.appendChild(status);

        if (isOnline) {
          setTimeout(() => status.remove(), 3000);
        }
      };

      window.addEventListener('online', () => showConnectionStatus(true));
      window.addEventListener('offline', () => showConnectionStatus(false));

      // Show initial status if offline
      if (!navigator.onLine) {
        showConnectionStatus(false);
      }
    }
  }

  // ⚡ PERFORMANCE OPTIMIZER
  class PerformanceOptimizer {
    constructor() {
      this.init();
    }

    init() {
      this.optimizeImages();
      this.addIntersectionObserver();
      this.optimizeAnimations();
      this.preloadCriticalResources();
    }

    optimizeImages() {
      const images = document.querySelectorAll('img');
      images.forEach(img => {
        // Add lazy loading
        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }

        // Add fade-in effect when loaded
        img.addEventListener('load', () => {
          img.style.opacity = '0';
          img.style.transition = 'opacity 0.3s ease';
          img.style.opacity = '1';
        });
      });
    }

    addIntersectionObserver() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');

            // Add staggered animation for child elements
            const children = entry.target.querySelectorAll('.animate-child');
            children.forEach((child, index) => {
              setTimeout(() => {
                child.classList.add('animate-in');
              }, index * 100);
            });
          }
        });
      }, observerOptions);

      // Observe all major sections
      const sections = document.querySelectorAll('section, article, .project-card, .stat-card');
      sections.forEach(section => observer.observe(section));
    }

    optimizeAnimations() {
      // Reduce animations for users who prefer reduced motion
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const style = document.createElement('style');
        style.textContent = `
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        `;
        document.head.appendChild(style);
      }
    }

    preloadCriticalResources() {
      // Preload critical CSS and JS
      const criticalResources = [
        { href: 'https://cdn.tailwindcss.com', as: 'style' },
        { href: 'https://cdn.jsdelivr.net/npm/marked/marked.min.js', as: 'script' }
      ];

      criticalResources.forEach(resource => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = resource.href;
        link.as = resource.as;
        document.head.appendChild(link);
      });
    }
  }

  // Start everything when DOM is ready
  init();

  // Hide loading screen after everything is initialized
  setTimeout(() => {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) {
      loadingScreen.style.opacity = '0';
      setTimeout(() => {
        loadingScreen.style.display = 'none';
      }, 500);
    }
  }, 1500);

  // Initialize independent scrolling
  new IndependentScrolling();

  // Initialize mobile app deep linking
  const mobileDeepLinks = new MobileAppDeepLinks();

  // Initialize GitHub projects and stats
  new GitHubProjects();

  // Initialize PWA Manager
  new PWAManager();

  // Initialize Performance Optimizer
  new PerformanceOptimizer();

  // === EMAILJS CONTACT FORM ===
  class EmailJSHandler {
    constructor() {
        // EmailJS credentials — use Vite env vars prefixed with VITE_
        // Create a `.env` with keys: VITE_EMAILJS_PUBLIC_KEY, VITE_EMAILJS_SERVICE_ID, VITE_EMAILJS_TEMPLATE_ID
        // Note: Vite only exposes env vars that start with `VITE_` to client code.
        this.publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';
        this.serviceID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
        this.templateID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      
      this.form = document.getElementById('contact-form');
      this.statusElement = document.getElementById('form-status');
      this.submitBtn = document.getElementById('submit-btn');
      this.btnText = document.getElementById('btn-text');
      this.btnLoading = document.getElementById('btn-loading');
      
      this.init();
    }

    init() {
      // Initialize EmailJS with your public key
      if (typeof emailjs !== 'undefined') {
        emailjs.init(this.publicKey);
        this.setupFormHandler();
      } else {
        console.error('EmailJS SDK not loaded');
        this.showStatus('Error: Email service not available', 'error');
      }
    }

    setupFormHandler() {
      if (this.form) {
        this.form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.sendEmail();
        });
      }
    }

    async sendEmail() {
      // Disable submit button and show loading state
      this.submitBtn.disabled = true;
      this.btnText.classList.add('hidden');
      this.btnLoading.classList.remove('hidden');
      this.statusElement.textContent = '';

      try {
        // Send email using EmailJS
        const response = await emailjs.sendForm(
          this.serviceID,
          this.templateID,
          this.form
        );

        console.log('Email sent successfully:', response);
        this.showStatus('Message sent successfully! I\'ll get back to you soon.', 'success');
        this.form.reset();
        
        // Show success animation
        this.showSuccessAnimation();

      } catch (error) {
        console.error('EmailJS error:', error);
        this.showStatus('Failed to send message. Please try again or email directly.', 'error');
      } finally {
        // Re-enable submit button
        this.submitBtn.disabled = false;
        this.btnText.classList.remove('hidden');
        this.btnLoading.classList.add('hidden');
      }
    }

    showStatus(message, type) {
      this.statusElement.textContent = message;
      this.statusElement.className = `text-center text-sm font-medium mt-2 ${
        type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
      }`;

      // Clear status after 5 seconds
      setTimeout(() => {
        this.statusElement.textContent = '';
      }, 5000);
    }

    showSuccessAnimation() {
      // Optional: Add a confetti or success animation here
      const successEmoji = document.createElement('div');
      successEmoji.textContent = '✓';
      successEmoji.className = 'text-4xl text-center animate-bounce';
      this.statusElement.appendChild(successEmoji);
      
      setTimeout(() => {
        successEmoji.remove();
      }, 2000);
    }
  }

  // Initialize EmailJS Handler
  new EmailJSHandler();

});